<script setup>
import { reactive, computed, onMounted, onBeforeUnmount, onUnmounted, watch, ref, nextTick } from 'vue'

import { useGlobalStore } from '@/stores/useGlobalStore'
import { useUserStore } from '@/stores/useUserStore'
import { useSpaceStore } from '@/stores/useSpaceStore'

import utils from '@/utils.js'
import consts from '@/consts.js'

import Links from '@/components/sidebar/Links.vue'
import Tags from '@/components/sidebar/Tags.vue'
import Removed from '@/components/sidebar/Removed.vue'
import Stats from '@/components/sidebar/Stats.vue'
import Inbox from '@/components/sidebar/Inbox.vue'
import Favorites from '@/components/sidebar/Favorites.vue'
import History from '@/components/sidebar/History.vue'
import Todos from '@/components/sidebar/Todos.vue'
import Note from '@/components/sidebar/Note.vue'
import SidebarResize from '@/components/SidebarResize.vue'

const globalStore = useGlobalStore()
const userStore = useUserStore()
const spaceStore = useSpaceStore()

const dialogElement = ref(null)
const titleElement = ref(null)
let unsubscribes

onMounted(() => {
  window.addEventListener('resize', updateDialogHeight)

  const globalActionUnsubscribe = globalStore.$onAction(
    ({ name, args }) => {
      if (name === 'triggerRemovedIsVisible') {
        toggleSection('removed')
      } else if (name === 'triggerNoteIsVisible') {
        toggleSection('note')
      }
    }
  )
  unsubscribes = () => {
    globalActionUnsubscribe()
  }
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', updateDialogHeight)
  unsubscribes()
})

const props = defineProps({
  visible: Boolean
})

const state = reactive({
  dialogHeight: null,
  tagsIsVisible: false,
  linksIsVisible: false,
  removedIsVisible: false,
  inboxIsVisible: false,
  statsIsVisible: false,
  favoritesIsVisible: false,
  historyIsVisible: false,
  todosIsVisible: false,
  noteIsVisible: false
})

watch(() => props.visible, (value, prevValue) => {
  if (value) {
    restoreUserLastSidebarSection()
    updateDialogHeight()
    globalStore.shouldExplicitlyHideFooter = true
  } else {
    globalStore.shouldExplicitlyHideFooter = false
  }
})

watch(() => userStore.sidebarResizeWidth, (value, prevValue) => {
  updateDialogHeight()
})

const clearVisible = () => {
  state.linksIsVisible = false
  state.tagsIsVisible = false
  state.removedIsVisible = false
  state.inboxIsVisible = false
  state.statsIsVisible = false
  state.favoritesIsVisible = false
  state.historyIsVisible = false
  state.todosIsVisible = false
  state.noteIsVisible = false
}
const updateSubsectionHeight = () => {
  const element = titleElement.value
  const rect = element.getBoundingClientRect()
  const height = rect.height
  state.subsectionHeight = state.dialogHeight - height
}
const updateDialogHeight = async () => {
  if (!props.visible) { return }
  await nextTick()
  const element = dialogElement.value
  state.dialogHeight = utils.elementHeight(element)
  await nextTick()
  updateSubsectionHeight()
}

// pin dialog

const dialogIsPinned = computed(() => globalStore.sidebarIsPinned)
const toggleDialogIsPinned = () => {
  const isPinned = !dialogIsPinned.value
  globalStore.sidebarIsPinned = isPinned
  setTimeout(() => {
    updateDialogHeight()
  }, 100) // wait for pinned dialog transition
}
const closeDialogs = () => {
  globalStore.tagDetailsIsVisible = false
  globalStore.triggerCloseChildDialogs()
}

// current section

const toggleSection = (value) => {
  clearVisible()
  state[`${value}IsVisible`] = true
  updateUserLastSidebarSection(value)
}

// last sidebar section

const restoreUserLastSidebarSection = () => {
  clearVisible()
  const section = userStore.lastSidebarSection
  const values = ['stats', 'inbox', 'removed', 'links', 'tags', 'favorites', 'history', 'todos', 'note'] // listed in api docs
  const isValid = values.includes(section)
  if (section && isValid) {
    state[section + 'IsVisible'] = true
  } else {
    state.inboxIsVisible = true
  }
}
const updateUserLastSidebarSection = (name) => {
  userStore.updateUser({ lastSidebarSection: name })
}

// styles

const styles = computed(() => {
  const minWidth = consts.defaultDialogWidth
  let width = userStore.sidebarResizeWidth || minWidth
  width = `${width}px`
  return {
    width,
    maxHeight: state.dialogHeight + 'px'
  }
})

</script>

<template lang="pug">
dialog#sidebar.sidebar.is-pinnable(
  v-if="visible"
  :open="visible"
  @click.left.stop="closeDialogs"
  ref="dialogElement"
  :style="styles"
  :data-is-pinned="dialogIsPinned"
  :class="{'is-pinned': dialogIsPinned}"
)
  section.title-section(ref="titleElement")
    .row.title-row-flex
      .button-wrap.segmented-buttons-wrap
        //- first row
        .segmented-buttons
          //- Inbox
          button(@click.left="toggleSection('inbox')" :class="{ active: state.inboxIsVisible}" title="Move from Inbox")
            img.icon(src="@/assets/inbox.svg")
          //- Favorites
          button(@click.left="toggleSection('favorites')" :class="{ active: state.favoritesIsVisible}" title="Favorites")
            img.icon(src="@/assets/heart-empty.svg")
          //- Stats
          button(@click.left="toggleSection('stats')" :class="{active: state.statsIsVisible}" title="Stats")
            img.icon.stats(src="@/assets/stats.svg")

          //- Tags
          button(@click.left="toggleSection('tags')" :class="{ active: state.tagsIsVisible}" title="Space Tags")
            span Tags
          //- Links
          button(@click.left="toggleSection('links')" :class="{ active: state.linksIsVisible}" title="Backlinks")
            span Links
        //- second row
        .segmented-buttons
          button(@click.left="toggleSection('todos')" :class="{ active: state.todosIsVisible}" title="Todos")
            span Todos
          button(@click.left="toggleSection('note')" :class="{ active: state.noteIsVisible}" title="Note")
            img.icon.note(src="@/assets/note.svg")
          //- Favorites
          button(@click.left="toggleSection('history')" :class="{ active: state.historyIsVisible}" title="Space History")
            img.icon.time(src="@/assets/time.svg")
          //- Removed
          button(@click.left="toggleSection('removed')" :class="{ active: state.removedIsVisible}" title="Removed Cards and Spaces")
            img.icon(src="@/assets/remove.svg")
            img.icon.remove-undo(src="@/assets/undo.svg")

      //- Pin
      .button-wrap.pin-button-wrap(@click.left="toggleDialogIsPinned" title="Pin dialog")
        button.small-button(:class="{active: dialogIsPinned}")
          img.icon.pin.right-pin(src="@/assets/pin.svg")

  Tags(:visible="state.tagsIsVisible" :parentIsPinned="dialogIsPinned" :subsectionHeight="state.subsectionHeight")
  Links(:visible="state.linksIsVisible" :parentIsPinned="dialogIsPinned" :subsectionHeight="state.subsectionHeight")
  Removed(:visible="state.removedIsVisible" :subsectionHeight="state.subsectionHeight")
  Stats(:visible="state.statsIsVisible" :subsectionHeight="state.subsectionHeight")
  Inbox(:visible="state.inboxIsVisible" :subsectionHeight="state.subsectionHeight")
  Favorites(:visible="state.favoritesIsVisible" :subsectionHeight="state.subsectionHeight")
  History(:visible="state.historyIsVisible" :subsectionHeight="state.subsectionHeight")
  Todos(:visible="state.todosIsVisible" :subsectionHeight="state.subsectionHeight")
  Note(:visible="state.noteIsVisible" :subsectionHeight="state.subsectionHeight")

  SidebarResize
</template>

<style lang="stylus">
.sidebar
  top calc(100% - 8px)
  left initial
  right 8px
  max-height calc(100vh - 25px)
  overflow hidden
  &.is-pinned
    top -36px
    right 0
  .title-row-flex
    align-items flex-start
  .right-pin
    transform rotate(180deg)
  .icon.flower
    vertical-align -1px
    height 11px
  .icon.time
    vertical-align -1px

  .segmented-buttons + .segmented-buttons
    margin-left 0

  .pin-button-wrap
    position absolute
    right 0
    top 0

  .segmented-buttons-wrap
    .segmented-buttons
      // &:first-child
      //   button,
      //   label
      //     &:last-child
      //       border-bottom-right-radius 0
      // middle row
      // &:nth-child(2)
      //   margin-top -1px
      //   button,
      //   label
      //     &:first-child
      //       border-top-left-radius 0
      //       border-bottom-left-radius 0
      //     // &:last-child
      //       // border-top-right-radius 0
      // last row
      &:last-child
        button,
        label
          &:first-child
            border-top-left-radius 0
          &:last-child
            border-top-right-radius 0

</style>
